import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { App } from './app/App';
import { AuthProvider } from './features/auth/AuthContext';
import { applyTenantTheme } from './lib/theme';
import { getSentryErrorBoundary, initSentry } from './lib/sentry';
import { initTelemetry } from './lib/telemetry';
import './index.css';

// Sentry primeiro: se VITE_SENTRY_DSN estiver setado, init capta erros
// já no boot. Sem DSN, é no-op.
void initSentry();

// PostHog telemetria — no-op se VITE_POSTHOG_KEY ausente. Import
// dinâmico mantém bundle pequeno quando não configurado.
void initTelemetry();

// Aplica tema do tenant (white-label) antes do primeiro render.
// Fire-and-forget — render não bloqueia, mudanças chegam via CSS vars.
applyTenantTheme().catch(() => undefined);

const qc = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

// Wrapper de ErrorBoundary: usa o do Sentry quando disponível, senão
// passa-through. Garante que crash de render vire evento (não tela branca).
const SentryErrorBoundary = getSentryErrorBoundary();
const Boundary: React.FC<{ children: React.ReactNode }> = SentryErrorBoundary
  ? ({ children }) => (
      <SentryErrorBoundary
        fallback={
          <div className="min-h-screen flex items-center justify-center p-8 text-center">
            <div>
              <h1 className="text-xl font-semibold mb-2">Algo deu errado</h1>
              <p className="text-ink-600 mb-4">
                Já fomos notificados. Recarregue a página para tentar de novo.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 rounded-md bg-brand-600 text-white"
              >
                Recarregar
              </button>
            </div>
          </div>
        }
      >
        {children}
      </SentryErrorBoundary>
    )
  : ({ children }) => <>{children}</>;

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Boundary>
      <QueryClientProvider client={qc}>
        <BrowserRouter
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </Boundary>
  </React.StrictMode>,
);
